import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Form } from "antd";
import { H3 } from "../../../components/Typography";
import { FormInstance } from "antd/lib/form/Form";
import { LMDropdown, LMNewInput } from "../../../components";
import {
  EProductTemperature,
  IProductFormValues,
  IProductCategory,
  EPickingType,
  IProductLabel,
} from "../types";
import { useAppNotifications, getErrorMessage } from "../../../components/LMNotifications";
import { createCategory, createProduct } from "../api";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../app/store";
import { fetchCategories, fetchLabels, refreshList } from "../slice";
import { IconPlus } from "../../../assets";
import { mapProductToCreate } from "./utils";
import { EMetadataTypes, IMetadataEntry } from "../../metadata/types";
import { getTranslation, setTranslation } from "../../i18n";
import { BarCodesSelector } from "../BarCodesSelector";
import styles from "./CreateProductForm.module.css";

const NEW_CATEGORY_VALUE = "create-new-category";

interface Props {
  onClose: () => void;
  form: FormInstance<IProductFormValues>;
  categories: IProductCategory[];
  labels: IProductLabel[];
  metadataEntries: IMetadataEntry[];
  onChangeLoading: (loading: boolean) => void;
}

export const CreateProductForm: FC<Props> = ({
  onClose,
  form,
  categories,
  labels,
  metadataEntries,
  onChangeLoading,
}) => {
  const { t } = useTranslation("productsPortfolio");
  const { contextHolder, success, error } = useAppNotifications();
  const dispatch = useDispatch<AppDispatch>();

  const getInitialValues = useCallback(() => {
    const values = { metadata: {} };
    for (const entry of metadataEntries) {
      if (entry.type === EMetadataTypes.Bool) {
        values.metadata = {
          ...values.metadata,
          [entry.name]: false,
        };
      }
    }
    return {
      ...values,
      temperature: EProductTemperature.Ambient,
      pickingType: EPickingType.Automated,
    };
  }, [metadataEntries]);

  const onFinish = async (values: IProductFormValues) => {
    onChangeLoading(true);
    const category = categories.find(
      (c) => c.id.toString() === values.categoryId
    );
    try {
      if (values.categoryName) {
        const category = await createCategory({
          name: setTranslation(values.categoryName),
        });
        await createProduct(
          mapProductToCreate({
            ...values,
            categoryName: getTranslation(category.data.name),
          })
        );
        await dispatch(fetchCategories()).unwrap();
        await dispatch(fetchLabels()).unwrap();
      } else {
        await createProduct(
          mapProductToCreate({
            ...values,
            categoryId: category ? category.id.toString() : undefined,
          })
        );
      }
      await dispatch(refreshList());
      form.resetFields();
      success(t("createProductSuccess"));
      onChangeLoading(false);
      onClose();
    } catch (e) {
      error(t("createProductFail"), getErrorMessage(e));
      onChangeLoading(false);
    }
  };

  const categoriesItems = categories.map((category) => ({
    name: getTranslation(category.name),
    value: category.id.toString(),
  }));

  const labelItems = labels.map((category) => ({
    name: getTranslation(category.name),
    value: category.id.toString(),
  }));

  const addCategoryItem = {
    name: t("createCategory"),
    value: NEW_CATEGORY_VALUE,
    icon: (
      <div className={styles["create-category-icon"]}>
        <IconPlus stroke="var(--color-primary-80)" />
      </div>
    ),
  };

  // const addLabelItem = {
  //   name: t("createLabel"),
  //   value: NEW_CATEGORY_VALUE,
  //   icon: (
  //     <div className={styles["create-category-icon"]}>
  //       <IconPlus stroke="var(--color-primary-80)" />
  //     </div>
  //   ),
  // };

  const category = Form.useWatch("categoryId", form);
  // const label = Form.useWatch("labels", form);

  return (
    <>
      {contextHolder}
      <div className={styles["confirmation-container"]}>
        <div className={styles["confirmation-title"]}>
          <H3>{t("createTitle")}</H3>
        </div>
        <Form
          layout={"vertical"}
          form={form}
          className={styles.form}
          initialValues={getInitialValues()}
          onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item name="name" label={t("productName")}
            rules={[{ required: true, message: t("requiredMessage") }]}
          >
            <LMNewInput placeholder={t("namePlaceholder")} />
          </Form.Item>
          <Form.Item name="description" label={t("productDescription")}>
            <LMNewInput placeholder={t("descriptionPlaceholder")} />
          </Form.Item>
          <Form.Item name="brand" label={t("brand")}>
            <LMNewInput placeholder={t("brandPlaceholder")} />
          </Form.Item>
          {/* <Form.Item name="image" label={t("productImage")}>
            <LMUpload />
          </Form.Item> */}
          {/* <Form.Item name="imageSize" label={t("imageSize")}>
            <LMNewInput placeholder={t("imageSizePlaceholder")} />
          </Form.Item> */}
          {/* <Form.Item
            name="categoryId"
            label={
              <div className={styles.category}>
                {t("category")}&nbsp;
                <Body2 className={styles.optional}>{t("optional")}</Body2>
              </div>
            }
          >
            <LMDropdown
              placeholder={t("dropdownPlaceholder")}
              items={[addCategoryItem, ...categoriesItems]}
            />
          </Form.Item>
          {category === NEW_CATEGORY_VALUE && (
            <Form.Item name="categoryName" label={t("categoryName")}>
              <LMNewInput placeholder={t("categoryNamePlaceholder")} />
            </Form.Item>
          )} */}
          {/* <Form.Item
            name="labels"
            label={
              <div className={styles.category}>
                {t("label")}&nbsp;
                <Body2 className={styles.optional}>{t("optional")}</Body2>
              </div>
            }
          >
            <LMDropdown
              placeholder={t("dropdownPlaceholder")}
              items={[addLabelItem, ...labelItems]}
            />
          </Form.Item> */}
          {/* {label === NEW_CATEGORY_VALUE && (
            <Form.Item name="labelName" label={t("labelName")}>
              <LMNewInput placeholder={t("labelNamePlaceholder")} />
            </Form.Item>
          )} */}
          {/* <Form.Item
            name="type"
            label={t("productType")}
            rules={[{ required: true, message: t("requiredMessage") }]}
          >
            <LMDropdown
              placeholder={t("dropdownPlaceholder")}
              items={Object.values(EProductType).map((value) => ({
                name: value,
                value,
              }))}
            />
          </Form.Item> */}
          <Form.Item name="pickingType" label={t("productPickingType")}>
            <LMDropdown
              placeholder={t("dropdownPlaceholder")}
              items={Object.values(EPickingType).map((value) => ({
                name: value,
                value,
              }))}
            />
          </Form.Item>
          <Form.Item name="temperature" label={t("temperature")}>
            <LMDropdown
              placeholder={t("dropdownPlaceholder")}
              items={Object.values(EProductTemperature).map((value) => ({
                name: value,
                value,
              }))}
            />
          </Form.Item>
          {/* <Form.Item
            name="nutrition"
            label={t("nutrition")}
            rules={[{ required: true, message: t("requiredMessage") }]}
          >
            <LMNewInput placeholder={t("nutritionPlaceholder")} />
          </Form.Item> */}
          <Form.Item name="price" label={t("price")}>
            <LMNewInput placeholder={t("pricePlaceholder")} />
          </Form.Item>
          <Form.Item name="barcodes" label="Barcodes">
            <BarCodesSelector />
          </Form.Item>
          <Form.Item name="heightMm" label={t("heightMm")}>
            <LMNewInput type="number" placeholder={t("heightMmPlaceholder")} />
          </Form.Item>
          <Form.Item name="widthMm" label={t("widthMm")}>
            <LMNewInput type="number" placeholder={t("widthMmPlaceholder")} />
          </Form.Item>
          <Form.Item name="lengthMm" label={t("lengthMm")}>
            <LMNewInput type="number" placeholder={t("lengthMmPlaceholder")} />
          </Form.Item>
          <Form.Item name="weightKg" label={t("weightKg")}>
            <LMNewInput type="number" placeholder={t("weightKgPlaceholder")} />
          </Form.Item>
          <Form.Item name="volumeL" label={t("volumeL")}>
            <LMNewInput type="number" placeholder={t("volumeLPlaceholder")} />
          </Form.Item>
          {/* <Form.Item name="external" valuePropName="checked">
            <div className={styles["check-container"]}>
              <LMCheck />{" "}
              <Button2 className={styles["check-label"]}>
                {t("external")}
              </Button2>
            </div>
          </Form.Item>
          <MetadataFields entries={metadataEntries} /> */}
        </Form>
      </div>
    </>
  );
};
